import { useState, useEffect, useRef, useCallback } from "react";

// ──────── Geometry Constants ────────
const VW = 520, VH = 310;
const CX = 260, CY = 290;
const R_OUTER = 225, R_INNER = 75;
const VISIBLE = 5;
const PAD_ANGLE = Math.PI * 0.14;
const USABLE_ARC = Math.PI - 2 * PAD_ANGLE;

const BAND_PATH = [
  `M ${CX - R_OUTER} ${CY}`, `A ${R_OUTER} ${R_OUTER} 0 0 1 ${CX + R_OUTER} ${CY}`,
  `L ${CX + R_INNER} ${CY}`, `A ${R_INNER} ${R_INNER} 0 0 0 ${CX - R_INNER} ${CY}`, `Z`
].join(' ');

const FRAME_OUTER = R_OUTER + 8, FRAME_INNER = R_INNER - 6;
const FRAME_PATH = [
  `M ${CX - FRAME_OUTER} ${CY}`, `A ${FRAME_OUTER} ${FRAME_OUTER} 0 0 1 ${CX + FRAME_OUTER} ${CY}`,
  `L ${CX + FRAME_INNER} ${CY}`, `A ${FRAME_INNER} ${FRAME_INNER} 0 0 0 ${CX - FRAME_INNER} ${CY}`, `Z`
].join(' ');

// ──────── Slot Computation Helpers ────────
function formatEventDate(ev) {
  const rawDate = ev.received_at || ev.created_at;
  if (!rawDate) return '';
  const d = new Date(rawDate);
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
    + ', ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase().replace(' ', '');
}

function computeSlotGeometry(slotPos, visCount) {
  const segAngle = (Math.PI - PAD_ANGLE) - ((slotPos + 0.5) / visCount) * USABLE_ARC;
  const isLeft = segAngle >= Math.PI / 2;
  const angleDeg = (segAngle * 180) / Math.PI;
  const rotation = isLeft ? (180 - angleDeg) : (360 - angleDeg);
  const rText = (R_OUTER * 0.7 + R_INNER * 0.3);
  const dotR = R_INNER + 22;

  const segArcLen = rText * (USABLE_ARC / visCount);
  const textH = 42;
  const rotRad = rotation * Math.PI / 180;
  const sinR = Math.abs(Math.sin(rotRad));
  const cosR = Math.abs(Math.cos(rotRad));
  const maxW_tangent = sinR > 0.15
    ? Math.max(40, (segArcLen - textH * cosR) / sinR)
    : 200;
  const maxW_radial = (R_OUTER - rText) * 2 - 10;
  const maxW = Math.min(maxW_tangent, maxW_radial);

  return {
    leftPct: ((CX + rText * Math.cos(segAngle)) / VW) * 100,
    topPct: ((CY - rText * Math.sin(segAngle)) / VH) * 100,
    dotLeftPct: ((CX + dotR * Math.cos(segAngle)) / VW) * 100,
    dotTopPct: ((CY - dotR * Math.sin(segAngle)) / VH) * 100,
    rotation, isLeft, segAngle,
    maxW: (maxW / VW) * 100,
  };
}

function drawDividers(svg, visCount) {
  const NS = 'http://www.w3.org/2000/svg';
  svg.querySelectorAll('[data-dynamic]').forEach(el => el.remove());
  for (let i = 0; i <= visCount; i++) {
    const angle = (Math.PI - PAD_ANGLE) - (i / visCount) * USABLE_ARC;
    const line = document.createElementNS(NS, 'line');
    line.setAttribute('x1', CX + (R_INNER + 1) * Math.cos(angle));
    line.setAttribute('y1', CY - (R_INNER + 1) * Math.sin(angle));
    line.setAttribute('x2', CX + (R_OUTER - 1) * Math.cos(angle));
    line.setAttribute('y2', CY - (R_OUTER - 1) * Math.sin(angle));
    line.setAttribute('stroke', '#c0beb8'); line.setAttribute('stroke-width', '0.8');
    line.setAttribute('opacity', '0.5'); line.setAttribute('data-dynamic', 'true');
    svg.appendChild(line);
  }
}

// ──────── Component ────────
export const SemicircleChart = ({ events }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const allEvents = events || [];
  const total = allEvents.length;
  const canScroll = total > VISIBLE;
  const maxOff = Math.max(0, total - VISIBLE);
  const visCount = Math.min(VISIBLE, total);

  const [scrollIdx, setScrollIdx] = useState(0);
  const scrollIdxRef = useRef(0);
  const dragRef = useRef({ active: false, startAngle: 0, startIdx: 0 });

  useEffect(() => { scrollIdxRef.current = scrollIdx; }, [scrollIdx]);

  const computeSlot = useCallback((ev, slotPos) => {
    const geo = computeSlotGeometry(slotPos, visCount);
    const isSuccess = ev.status === 'success' || ev.status === 'deployed';
    return {
      ...geo,
      isSuccess,
      line1: isSuccess ? 'deployed' : 'failed',
      line2: ev.dry_run ? 'SAFE PREVIEW' : 'LIVE',
      line3: formatEventDate(ev),
    };
  }, [visCount]);

  // Draw SVG divider lines
  useEffect(() => {
    if (svgRef.current && visCount > 0) drawDividers(svgRef.current, visCount);
  }, [visCount]);

  // Mouse wheel scroll
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !canScroll) return;
    const handler = (e) => {
      const delta = Math.sign(e.deltaY);
      const cur = scrollIdxRef.current;
      const next = cur + delta;
      if (next < 0 || next > maxOff) return;
      e.preventDefault();
      e.stopPropagation();
      scrollIdxRef.current = next;
      setScrollIdx(next);
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [canScroll, maxOff]);

  const getAngle = useCallback((clientX, clientY) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    return Math.atan2((rect.top + rect.height * 0.935) - clientY, clientX - (rect.left + rect.width / 2));
  }, []);

  const startDrag = useCallback((clientX, clientY) => {
    if (!canScroll) return;
    dragRef.current = { active: true, startAngle: getAngle(clientX, clientY), startIdx: scrollIdxRef.current };
  }, [canScroll, getAngle]);

  const moveDrag = useCallback((clientX, clientY) => {
    if (!dragRef.current.active) return;
    const delta = getAngle(clientX, clientY) - dragRef.current.startAngle;
    const slots = Math.round(delta / (Math.PI / VISIBLE));
    const next = Math.max(0, Math.min(maxOff, dragRef.current.startIdx - slots));
    if (next !== scrollIdxRef.current) { scrollIdxRef.current = next; setScrollIdx(next); }
  }, [getAngle, maxOff]);

  const endDrag = useCallback(() => { dragRef.current.active = false; }, []);

  // Global mouse drag handlers
  useEffect(() => {
    const onMove = (e) => { if (dragRef.current.active) { moveDrag(e.clientX, e.clientY); e.preventDefault(); } };
    const onUp = () => endDrag();
    window.addEventListener('mousemove', onMove, { passive: false });
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [moveDrag, endDrag]);

  // Touch drag handlers
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !canScroll) return;
    const onStart = (e) => { const t = e.touches[0]; startDrag(t.clientX, t.clientY); };
    const onMove = (e) => { if (dragRef.current.active) { const t = e.touches[0]; moveDrag(t.clientX, t.clientY); e.preventDefault(); } };
    const onEnd = () => endDrag();
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    return () => { el.removeEventListener('touchstart', onStart); el.removeEventListener('touchmove', onMove); el.removeEventListener('touchend', onEnd); };
  }, [canScroll, startDrag, moveDrag, endDrag]);

  const onKeyDown = useCallback((e) => {
    if (!canScroll) return;
    let delta = 0;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') delta = -1;
    else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') delta = 1;
    if (delta) {
      e.preventDefault();
      setScrollIdx(prev => { const n = Math.max(0, Math.min(maxOff, prev + delta)); scrollIdxRef.current = n; return n; });
    }
  }, [canScroll, maxOff]);

  if (total === 0) return <div style={{textAlign:'center',padding:'40px 0',color:'var(--text-muted)',fontSize:'13px'}}>No webhook activity yet</div>;

  const rangeStart = Math.max(0, scrollIdx - 1);
  const rangeEnd = Math.min(total, scrollIdx + VISIBLE + 1);

  return (
    <div
      ref={containerRef}
      tabIndex={canScroll ? 0 : undefined}
      onKeyDown={canScroll ? onKeyDown : undefined}
      onMouseDown={canScroll ? (e) => { startDrag(e.clientX, e.clientY); containerRef.current?.focus(); e.preventDefault(); } : undefined}
      style={{ position: 'relative', maxWidth: '600px', margin: '0 auto', padding: '0 12px', cursor: canScroll ? (dragRef.current.active ? 'grabbing' : 'grab') : 'default', outline: 'none', userSelect: 'none' }}
      data-testid="semicircle-wheel"
    >
      <svg ref={svgRef} viewBox={`0 0 ${VW} ${VH}`} style={{ width: '100%', display: 'block', pointerEvents: 'none' }}>
        <defs>
          <linearGradient id="chromeFrame" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#e8e6e0" /><stop offset="20%" stopColor="#d0cec6" />
            <stop offset="40%" stopColor="#b8b6ae" /><stop offset="60%" stopColor="#a8a69e" />
            <stop offset="80%" stopColor="#c0beb6" /><stop offset="100%" stopColor="#d8d6ce" />
          </linearGradient>
          <radialGradient id="bandFill" cx="50%" cy="100%" r="80%">
            <stop offset="0%" stopColor="#faf8f2" /><stop offset="50%" stopColor="#f4f2ec" /><stop offset="100%" stopColor="#eceae4" />
          </radialGradient>
          <linearGradient id="glassHL" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="white" stopOpacity="0.45" /><stop offset="25%" stopColor="white" stopOpacity="0.12" />
            <stop offset="55%" stopColor="white" stopOpacity="0" /><stop offset="100%" stopColor="black" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={FRAME_PATH} fill="url(#chromeFrame)" stroke="#9a9890" strokeWidth="0.5" opacity="0.65" />
        <path d={BAND_PATH} fill="url(#bandFill)" stroke="#c8c6be" strokeWidth="1" />
        <path d={BAND_PATH} fill="url(#glassHL)" style={{ pointerEvents: 'none' }} />
        <path d={`M ${CX - R_OUTER + 16} ${CY - 5} A ${R_OUTER - 10} ${R_OUTER - 10} 0 0 1 ${CX + R_OUTER - 16} ${CY - 5}`}
          fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round" style={{ pointerEvents: 'none' }} />
        <path d={`M ${CX - R_INNER + 4} ${CY} A ${R_INNER - 4} ${R_INNER - 4} 0 0 1 ${CX + R_INNER - 4} ${CY}`}
          fill="none" stroke="#d4d2cc" strokeWidth="1" strokeDasharray="3 5" opacity="0.35" />
      </svg>

      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}>
        {Array.from({ length: rangeEnd - rangeStart }, (_, i) => {
          const gIdx = rangeStart + i;
          const ev = allEvents[gIdx];
          const slotPos = gIdx - scrollIdx;
          const isVis = slotPos >= 0 && slotPos < visCount;
          const s = computeSlot(ev, slotPos);

          return (
            <div key={gIdx} style={{ pointerEvents: 'none' }}>
              <div data-testid={`webhook-dot-${gIdx}`} style={{
                position: 'absolute', left: `${s.dotLeftPct}%`, top: `${s.dotTopPct}%`,
                width: '7px', height: '7px', borderRadius: '50%',
                backgroundColor: s.isSuccess ? '#00cc33' : '#e04040',
                boxShadow: `0 0 6px ${s.isSuccess ? 'rgba(0,204,50,0.5)' : 'rgba(224,64,64,0.5)'}, 0 0 12px ${s.isSuccess ? 'rgba(0,204,50,0.25)' : 'rgba(224,64,64,0.25)'}`,
                transform: 'translate(-50%, -50%)', opacity: isVis ? 1 : 0,
                transition: 'left 0.4s ease-out, top 0.4s ease-out, opacity 0.25s ease-out',
              }} />
              <div data-testid={`webhook-label-${gIdx}`} style={{
                position: 'absolute', left: `${s.leftPct}%`, top: `${s.topPct}%`,
                transform: `translate(-50%, -50%) rotate(${s.rotation}deg)`,
                opacity: isVis ? 1 : 0,
                transition: 'left 0.4s ease-out, top 0.4s ease-out, transform 0.4s ease-out, opacity 0.25s ease-out',
                whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: `${s.maxW}%`,
                lineHeight: '1.1', textAlign: s.isLeft ? 'right' : 'left',
                fontFamily: 'system-ui, -apple-system, sans-serif',
              }}>
                {s.isLeft ? (
                  <>
                    <div style={{ fontSize: '13px', fontWeight: 800, color: '#1a1810', fontStyle: 'italic', letterSpacing: '0.5px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.line1}</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#3a3830', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.line2}</div>
                    <div style={{ fontSize: '11px', fontWeight: 500, color: '#8a8580', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.line3}</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '11px', fontWeight: 500, color: '#8a8580', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.line3}</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#3a3830', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.line2}</div>
                    <div style={{ fontSize: '13px', fontWeight: 800, color: '#1a1810', fontStyle: 'italic', letterSpacing: '0.5px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.line1}</div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {canScroll && (
        <div data-testid="webhook-scroll-indicator" style={{
          textAlign: 'center', marginTop: '6px', fontSize: '11px', color: '#9a9590',
          fontFamily: 'system-ui, -apple-system, sans-serif', letterSpacing: '0.3px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        }}>
          <span style={{ opacity: scrollIdx > 0 ? 0.8 : 0.25, transition: 'opacity 0.2s', fontSize: '13px' }}>&#9664;</span>
          <span>{scrollIdx + 1}&ndash;{Math.min(scrollIdx + VISIBLE, total)} of {total}</span>
          <span style={{ opacity: scrollIdx < maxOff ? 0.8 : 0.25, transition: 'opacity 0.2s', fontSize: '13px' }}>&#9654;</span>
        </div>
      )}
    </div>
  );
};
